import { Link } from 'react-router-dom';

import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import PublicReveal from './PublicReveal';
import { resolveTrackCoverImage } from '../../utils/trackCoverImage';
import type { PublicFiliereTrack } from '../../utils/publicFilieres';
import {
  getTrackInscriptionAvailability,
  type FiliereCohorteAvailabilityRow,
} from '../../utils/filiereInscriptionAvailability';
import '../../styles/publicFiliereTrackPage.css';

export type PublicFiliereTrackHeroProps = {
  track: PublicFiliereTrack;
  cohortes?: FiliereCohorteAvailabilityRow[];
};

export default function PublicFiliereTrackHero({ track, cohortes }: PublicFiliereTrackHeroProps) {
  const parcoursCount = track.filieres.length;
  const heroImage = resolveTrackCoverImage(track);
  const inscription = getTrackInscriptionAvailability(
    track.filieres.map((f) => f.id),
    cohortes,
  );

  return (
    <section className="public-track-hero">
      <div className="public-track-hero__blob-primary public-track-organic-hero-bg -z-10" aria-hidden />
      <div className="public-track-hero__blob-secondary -z-10" aria-hidden />

      <div className="public-track-hero__layout">
        <PublicReveal animation="fade-in-left" className="public-track-hero__copy space-y-6">
          <nav className="text-sm text-on-surface-variant" aria-label="Fil d'Ariane">
            <Link to="/" className="font-medium text-primary hover:underline">
              Accueil
            </Link>
            <span className="mx-2 text-on-surface-variant/60">/</span>
            <Link to="/#filieres" className="font-medium text-primary hover:underline">
              Domaines
            </Link>
            <span className="mx-2 text-on-surface-variant/60">/</span>
            <span className="text-slate-900">{track.title}</span>
          </nav>

          <div className="space-y-5">
            <span className="inline-block rounded-full bg-primary/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-primary">
              Domaine de formation
            </span>
            <h1 className="max-w-xl text-3xl font-extrabold leading-tight tracking-tight text-on-background sm:text-4xl lg:text-5xl">
              {track.title}
            </h1>
            {track.intro?.trim() ? (
              <p className="max-w-lg whitespace-pre-line text-base leading-relaxed text-on-surface-variant sm:text-lg">
                {track.intro.trim()}
              </p>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-5 pt-1">
            <Link
              to="/login?mode=register"
              className="public-track-soft-bloom inline-flex rounded-public-btn bg-primary px-8 py-4 text-sm font-bold text-white shadow-sm"
            >
              Créer mon compte
            </Link>
            <a
              href="#programmes"
              className="group flex cursor-pointer items-center gap-3"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-primary/30 text-primary transition-colors group-hover:bg-primary group-hover:text-white">
                <LucideSymbolIcon name="expand_more" tone="inherit" size={22} className="text-current" />
              </span>
              <span className="text-sm font-bold text-on-background transition-colors group-hover:text-primary">Voir les programmes</span>
            </a>
          </div>
        </PublicReveal>

        <div className="public-track-hero__media mt-4 lg:mt-0">
          <div className="public-track-hero__media-stage">
            <PublicReveal animation="fade-in" className="public-track-hero__media-reveal">
              <div className="public-track-hero__media-frame">
                <div className="public-track-organic-shape public-track-hero__media-shadow" aria-hidden />

                <div className="public-track-hero__media-surface overflow-hidden rounded-public-card bg-surface-container-high">
                  <img
                    src={heroImage}
                    alt=""
                    className="h-full w-full object-cover"
                    loading="eager"
                    decoding="async"
                  />
                </div>
              </div>
            </PublicReveal>

            <div className="public-track-hero__float-card public-track-hero__float-card--stats flex items-center gap-4 rounded-public-card bg-white p-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <LucideSymbolIcon name="school" tone="primary" size={22} />
              </div>
              <div>
                <p className="text-xs font-medium leading-tight text-on-surface-variant">Formations actives</p>
                <p className="font-bold text-on-background">{parcoursCount}</p>
              </div>
            </div>

            <div className="public-track-hero__float-card public-track-hero__float-card--inscriptions flex flex-col gap-2 rounded-public-card bg-white p-5">
              <p className="text-sm font-bold text-on-background">Inscriptions</p>
              <p className="text-xs font-semibold text-primary">{inscription.label}</p>
              <div className="flex h-10 items-end gap-1.5">
                <div className="h-1/3 w-2.5 rounded-full bg-primary/20" />
                <div className="h-1/2 w-2.5 rounded-full bg-primary/40" />
                <div className="h-3/4 w-2.5 rounded-full bg-primary/70" />
                <div className="h-full w-2.5 rounded-full bg-primary" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
